import type {
  ReviewFilterOrderOptions,
  ReviewFilterSortOptions,
  ReviewSourceSystem,
  ReviewType,
} from "@/services/bedrock";
import type { ReviewSentimentFilter, ReviewTriageFilter } from "@/components/reviews";
import type { ReviewDatePreset } from "@/components/reviews";

export type CoachNpsCategoryFilter = "promoter" | "passive" | "detractor";
export type CoachRecommendationFilter = "positive" | "negative";

export type CoachReviewFilterState = {
  search: string;
  sourceSystem: ReviewSourceSystem | null;
  ratingType: ReviewType | null;
  ratingValue: number | null;
  npsCategory: CoachNpsCategoryFilter | null;
  recommendation: CoachRecommendationFilter | null;
  sentiment: ReviewSentimentFilter | null;
  triage: ReviewTriageFilter | null;
  datePreset: ReviewDatePreset | null;
  startDate: string | null;
  endDate: string | null;
  /** Null means no Coach-selected sort; the API still defaults to date descending. */
  sort: ReviewFilterSortOptions | null;
  order: ReviewFilterOrderOptions;
};

export type CoachReviewUrlState = CoachReviewFilterState & {
  playerId: number | null;
  segmentationIds: number[];
  perPage: number;
  reviewId: string | null;
};

export const DEFAULT_REVIEW_URL_STATE: CoachReviewUrlState = {
  search: "",
  sourceSystem: null,
  ratingType: null,
  ratingValue: null,
  npsCategory: null,
  recommendation: null,
  sentiment: null,
  triage: null,
  datePreset: null,
  startDate: null,
  endDate: null,
  sort: null,
  order: "desc",
  playerId: null,
  segmentationIds: [],
  perPage: 25,
  reviewId: null,
};

export const DEFAULT_REVIEW_SORT: ReviewFilterSortOptions = "effective_date";
export const DEFAULT_REVIEW_ORDER: ReviewFilterOrderOptions = "desc";

const SORT_OPTIONS: ReviewFilterSortOptions[] = [
  "effective_date",
  "star_rating",
  "ten_point_rating",
  "nps_score",
  "display_name",
  "content",
  "sentiment",
  "player_name",
  "ai_shareability_score",
  "sentiment_shareability",
];

const SOURCE_SYSTEMS: ReviewSourceSystem[] = ["FB", "ZL", "GL", "SC", "BE", "EX", "IF"];
const RATING_TYPES: ReviewType[] = ["star", "nps", "ten_point", "text"];
const NPS_CATEGORIES: CoachNpsCategoryFilter[] = ["promoter", "passive", "detractor"];
const RECOMMENDATIONS: CoachRecommendationFilter[] = ["positive", "negative"];
const SENTIMENTS: ReviewSentimentFilter[] = ["positive", "neutral", "negative"];
const TRIAGES: ReviewTriageFilter[] = ["needingReply", "replied", "skipped"];
const DATE_PRESETS: ReviewDatePreset[] = [
  "last_7_days",
  "last_30_days",
  "this_month",
  "last_month",
  "two_months_ago",
  "ytd",
  "custom",
];

function parseEnum<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  if (!value) return null;
  return allowed.includes(value as T) ? (value as T) : null;
}

function parseNumber(value: string | null): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseNumberList(value: string | null): number[] {
  if (!value) return [];
  return value
    .split(",")
    .map(part => Number(part.trim()))
    .filter(n => Number.isFinite(n));
}

export function parseCoachReviewSearch(search: string): CoachReviewUrlState {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const state: CoachReviewUrlState = { ...DEFAULT_REVIEW_URL_STATE };

  state.search = params.get("search") ?? "";
  state.sourceSystem = parseEnum(params.get("sourceSystem"), SOURCE_SYSTEMS);
  const rawRatingType = params.get("ratingType");
  // Back-compat: older URLs used "recommendation" for ReviewType "text"
  state.ratingType = parseEnum(rawRatingType === "recommendation" ? "text" : rawRatingType, RATING_TYPES);
  state.ratingValue = parseNumber(params.get("ratingValue"));
  state.npsCategory = parseEnum(params.get("npsCategory"), NPS_CATEGORIES);
  state.recommendation = parseEnum(params.get("recommendation"), RECOMMENDATIONS);
  state.sentiment = parseEnum(params.get("sentiment"), SENTIMENTS);
  state.triage = parseEnum(params.get("triage"), TRIAGES);
  state.datePreset = parseEnum(params.get("datePreset"), DATE_PRESETS);
  state.startDate = params.get("startDate") || null;
  state.endDate = params.get("endDate") || null;
  if ((state.startDate || state.endDate) && !state.datePreset) {
    state.datePreset = "custom";
  }
  state.sort = parseEnum(params.get("sort"), SORT_OPTIONS);
  state.order = parseEnum(params.get("order"), ["asc", "desc"] as ReviewFilterOrderOptions[]) ?? DEFAULT_REVIEW_ORDER;
  state.playerId = parseNumber(params.get("playerId"));
  state.segmentationIds = parseNumberList(params.get("segmentationIds"));
  state.perPage = Math.max(1, parseNumber(params.get("perPage")) ?? DEFAULT_REVIEW_URL_STATE.perPage);
  state.reviewId = params.get("reviewId") || null;

  return state;
}

export function serializeCoachReviewSearch(state: CoachReviewUrlState): string {
  const params = new URLSearchParams();

  if (state.search.trim()) params.set("search", state.search.trim());
  if (state.sourceSystem) params.set("sourceSystem", state.sourceSystem);
  if (state.ratingType) params.set("ratingType", state.ratingType);
  if (state.ratingValue != null) params.set("ratingValue", String(state.ratingValue));
  if (state.npsCategory) params.set("npsCategory", state.npsCategory);
  if (state.recommendation) params.set("recommendation", state.recommendation);
  if (state.sentiment) params.set("sentiment", state.sentiment);
  if (state.triage) params.set("triage", state.triage);
  if (state.datePreset) params.set("datePreset", state.datePreset);
  if (state.startDate) params.set("startDate", state.startDate);
  if (state.endDate) params.set("endDate", state.endDate);
  if (state.sort) {
    params.set("sort", state.sort);
    params.set("order", state.order);
  }
  if (state.playerId != null) params.set("playerId", String(state.playerId));
  if (state.segmentationIds.length) params.set("segmentationIds", state.segmentationIds.join(","));
  if (state.perPage !== DEFAULT_REVIEW_URL_STATE.perPage) params.set("perPage", String(state.perPage));
  if (state.reviewId) params.set("reviewId", state.reviewId);

  return params.toString();
}
